import { CUTTING_SHAPE_ID, GRID_HELPER_ID } from '@/domain/selection/reservedSelectionIds';
import { CUTTING_MASK_NAME, CUTTING_OUTLINE_NAME } from '@/infrastructure/engine2d/fabricGroupsName';
import { convertFromLowDPIToHighDPI } from '@/utils/convertmmPixel';
import { createCuttingGroup, getFabricGroupById } from '@/infrastructure/engine2d/helpers/FabricGroupHelper';
import { FabricObject, loadSVGFromURL, Rect, util } from 'fabric';
import type { FabricScene } from '@/infrastructure/engine2d/FabricScene';
import type { IGroup } from '@/infrastructure/engine2d/types/IGroup';

const CUT_PATH_STROKE_SCALE_RATIO = 3000;
const CUT_PATH_STROKE_COLOR = '#00C0FA';

function _outlineStrokeWidth(width: number): number {
    return width / CUT_PATH_STROKE_SCALE_RATIO;
}

// Nothing about the cut shape is editable on the canvas, so the choice here is purely what
// to draw: `maskOutsideCut` covers everything falling outside the silhouette with a white
// mask and strokes the outline on top — the end-user preview of what will be cut away.
// Without it only the outline is drawn, over an unmasked canvas, which is what an admin
// needs while setting the product's cut shape. The caller resolves which one it wants, so
// this factory never sees user context.
function paintCuttingShape(svgUrl: string, fabricWorld: FabricScene, maskOutsideCut: boolean): Promise<void> | undefined {
    if (maskOutsideCut) {
        return _addMaskedCuttingPath(svgUrl, fabricWorld);
    }
    return _addCuttingPathOutline(svgUrl, fabricWorld);
}

async function _addCuttingPathOutline(svgUrl: string, fabricWorld: FabricScene): Promise<void> {
    const { fabricCanvas } = fabricWorld;
    let cuttingGroup = getFabricGroupById(fabricCanvas, CUTTING_SHAPE_ID);
    if (!cuttingGroup) {
        cuttingGroup = createCuttingGroup(fabricCanvas, CUTTING_SHAPE_ID);
    }
    const svgGroup = util.groupSVGElements([], {
        absolutePositioned: true,
        inverted: true,
    }) as IGroup;
    if (cuttingGroup.getObjects().length !== 0) {
        cuttingGroup.remove(...cuttingGroup.getObjects());
    }
    // The cut shape is product configuration, not content: it can't be moved, scaled or
    // rotated, and deleting it is the topbar's job. Making it selectable only ever drew
    // nine inert transform handles, so both groups stay out of the way entirely — which
    // also makes the five lock flags this used to set unnecessary.
    svgGroup.selectable = false;
    svgGroup.evented = false;
    cuttingGroup.selectable = false;
    cuttingGroup.evented = false;
    cuttingGroup.add(svgGroup);

    const svgfieldToSvgUrl = new URL(svgUrl, import.meta.url).href;
    await loadSVGFromURL(svgfieldToSvgUrl, (_svgObject, fabricPathObject) => {
        fabricPathObject.set({
            fill: '',
            stroke: CUT_PATH_STROKE_COLOR,
            strokeWidth: _outlineStrokeWidth(fabricWorld.imageRenderWidth),
            scaleX: convertFromLowDPIToHighDPI(1),
            scaleY: convertFromLowDPIToHighDPI(1),
        });
        svgGroup.add(fabricPathObject);
    });
    // TODO Multi-shape: remove scale for each shape and apply it to the group only.
    // Fabric group doesn't take size according to scale but to children shapes size.
    // Paused until client review on single-shape cutting path completes.
    cuttingGroup.set('left', fabricWorld.imageRenderWidth / 2);
    cuttingGroup.set('top', fabricWorld.imageRenderHeight / 2);
    // Reset to identity: the group is reused between paints, and a previous verso paint
    // leaves scaleX negative from the mirroring.
    cuttingGroup.set('angle', 0);
    cuttingGroup.set('scaleX', 1);
    cuttingGroup.set('scaleY', 1);
    cuttingGroup.set('originX', 'center');
    cuttingGroup.set('originY', 'center');
    cuttingGroup.setCoords();
    cuttingGroup.set('dirty', true);
    fabricCanvas.bringObjectToFront(cuttingGroup);
    fabricCanvas.renderAll();
}

async function _addMaskedCuttingPath(svgUrl: string, fabricWorld: FabricScene): Promise<void> {
    const { fabricCanvas } = fabricWorld;
    let cuttingGroup = getFabricGroupById(fabricCanvas, CUTTING_SHAPE_ID);
    if (!cuttingGroup) {
        cuttingGroup = createCuttingGroup(fabricCanvas, CUTTING_SHAPE_ID);
    }
    let { height, width } = fabricCanvas;
    if (cuttingGroup.getObjects().length !== 0) {
        cuttingGroup.remove(...cuttingGroup.getObjects());
    }
    const bgGroup = getFabricGroupById(fabricCanvas);
    if (bgGroup) {
        height = bgGroup.height * bgGroup.scaleY;
        width = bgGroup.width * bgGroup.scaleX;
    }
    height = Math.floor(height);
    width = Math.floor(width);
    cuttingGroup.set('objectCaching', false);

    const rect = new Rect({
        id: CUTTING_MASK_NAME,
        evented: false,
        fill: 'white',
        strokeWidth: 0,
        height: height,
        left: 0,
        selectable: false,
        top: 0,
        width: width,
        objectCaching: false,
    });
    cuttingGroup.add(rect);

    const svgOutlineGroup = util.groupSVGElements([], {
        absolutePositioned: true,
        selectable: false,
        evented: false,
        left: Math.floor(width / 2),
        top: Math.floor(height / 2),
        originX: 'center',
        originY: 'center',
        objectCaching: false,
    });

    svgOutlineGroup.set('id', CUTTING_OUTLINE_NAME);

    fabricCanvas.add(svgOutlineGroup);

    const clipPathGroup = util.groupSVGElements([], {
        absolutePositioned: true,
        inverted: true,
    });
    cuttingGroup.clipPath = clipPathGroup;

    const svgfieldToSvgUrl = new URL(svgUrl, import.meta.url).href;
    await loadSVGFromURL(svgfieldToSvgUrl, (_svgObject, fabricPathObject) => {
        fabricPathObject.objectCaching = true;
        fabricPathObject.set({
            fill: '#000000',
            originX: 'center',
            originY: 'center',
            scaleX: convertFromLowDPIToHighDPI(1),
            scaleY: convertFromLowDPIToHighDPI(1),
            strokeWidth: 0,
        });
        (clipPathGroup as IGroup).add(fabricPathObject);

        fabricPathObject.clone().then((clonedPath: FabricObject) => {
            clonedPath.set({
                stroke: CUT_PATH_STROKE_COLOR,
                fill: '',
                strokeWidth: _outlineStrokeWidth(width),
            });
            (svgOutlineGroup as IGroup).add(clonedPath);
        });
    });

    clipPathGroup.set('left', Math.floor(width / 2));
    clipPathGroup.set('top', Math.floor(height / 2));
    clipPathGroup.set('originX', 'center');
    clipPathGroup.set('originY', 'center');
    clipPathGroup.set('dirty', true);
    clipPathGroup.setCoords();

    svgOutlineGroup.set('left', Math.floor(width / 2));
    svgOutlineGroup.set('top', Math.floor(height / 2));
    svgOutlineGroup.set('dirty', true);
    svgOutlineGroup.setCoords();

    fabricCanvas.bringObjectToFront(cuttingGroup);
    const gridImage = fabricCanvas.getObjects().find((obj) => (obj as { id?: string }).id === GRID_HELPER_ID);
    if (gridImage) {
        fabricCanvas.bringObjectToFront(gridImage);
    }
    fabricCanvas.bringObjectToFront(svgOutlineGroup);

    fabricCanvas.renderAll();
}

async function paintMirroredCuttingShape(svgUrl: string, fabricWorld: FabricScene, maskOutsideCut: boolean): Promise<void> {
    await paintCuttingShape(svgUrl, fabricWorld, maskOutsideCut);
    const { fabricCanvas } = fabricWorld;
    const cuttingGroup = getFabricGroupById(fabricCanvas, CUTTING_SHAPE_ID);
    if (!cuttingGroup) {
        console.error('Cannot mirror the cutting path: the paint left no cutting group on the canvas');
        return;
    }
    if (cuttingGroup.clipPath) {
        cuttingGroup.clipPath.set('scaleX', cuttingGroup.clipPath.scaleX * -1);
    }
    cuttingGroup.set('scaleX', cuttingGroup.scaleX * -1);
    cuttingGroup.setCoords();
    cuttingGroup.set('dirty', true);

    // Only the masked render builds a separate outline group; the outline-only render
    // strokes the paths inside the cutting group itself. Requiring the outline here used to
    // abort the whole mirroring in that case, leaving the admin an unmirrored verso.
    const cuttingGroupOutline = getFabricGroupById(fabricCanvas, CUTTING_OUTLINE_NAME);
    if (cuttingGroupOutline) {
        cuttingGroupOutline.set('scaleX', cuttingGroupOutline.scaleX * -1);
        cuttingGroupOutline.setCoords();
        cuttingGroupOutline.set('dirty', true);
    }

    fabricCanvas.renderAll();
}

export { paintCuttingShape, paintMirroredCuttingShape };
